/**
 * Contract for the aggregator credentials form, mirroring
 * `WebApiAggregatorServices/.../Models/AggregatorCredentialsForm.cs` and
 * `AggregatorCredentialsFormField.cs` (noodle-soup).
 *
 * The backend describes what to ask the user; the frontend only renders fields
 * and echoes the model back with `Value` filled in. Adding an aggregator is
 * therefore free on this side, as long as it speaks this contract.
 */

/** Aggregators an investor can connect today (uspGetFinancialInstitutionsForSearch). */
export enum AggregatorId {
  ByAll = 4,
  Demo = 5,
  Yodlee = 30,
}

/**
 * C# implicit enum — the NUMBERS travel on the wire, so the order is the contract.
 * Declaration order in Models/AggregatorCredentialsForm.cs.
 */
export enum AggregatorCredentialsFormType {
  AddCredentials = 0,
  UpdateCredentials = 1,
  MfaCredentials = 2,
  ManualMfaCredentials = 3,
  AuthenticationSucceeded = 4,
  ManualAccounts = 5,
  UpdateUserInput = 6,
  NotRecoverable = 7,
  CustomMessage = 8,
  OauthCredentials = 9,
}

/** Models/AggregatorCredentialsFormField.cs — also 0-based and order-dependent. */
export enum AggregatorCredentialsFieldType {
  Text = 0,
  Password = 1,
  RadioGroup = 2,
  Checkboxgroup = 3,
  Image = 4,
  Link = 5,
}

/** Models/AggregatorStatus.cs — note this one is 1-based, unlike the two above. */
export enum AggregatorFormStatus {
  Success = 1,
  Failure = 2,
  MoreInfoRequired = 3,
  NotRecoverable = 4,
  CustomMessage = 5,
}

export interface AggregatorChoice {
  Label: string;
  Value: string;
}

export interface AggregatorCredentialFormField {
  Display: string;
  Value: string | null;
  FieldType: AggregatorCredentialsFieldType;
  FieldName: string;
  Instructions?: string | null;
  Choices?: AggregatorChoice[] | null;
  /** `byte[]` server-side, so it arrives as base64 — or already as a data URI. */
  Image?: string | null;
  HTML?: string | null;
  Id: number;
}

export interface AggregatorStatus {
  StatusCode: AggregatorFormStatus;
  Message?: string | null;
}

export interface AggregatorCredentialsForm {
  CredentialFormFields?: AggregatorCredentialFormField[] | null;
  Type: AggregatorCredentialsFormType;
  Status: AggregatorStatus;
  Instructions?: string | null;
}

/**
 * The envelope that travels in both directions: the backend returns it, the
 * frontend fills in each field's `Value` and PUTs the whole thing back. Mirrors
 * `AggregatorModel.cs`.
 *
 * `AdditionalAccounts`, `Institution` and `Role` are deliberately absent: this
 * flow never reads them, and the backend only populates them on the add-account
 * path. They still round-trip untouched, because the submit spreads the cached
 * response (`{...model}`) and a property the interface does not declare is still
 * copied at runtime.
 */
export interface AggregatorCredentialsModel {
  WealthId: number;
  AggregatorId: number;
  FinancialInstitutionId: number;
  CredentialsForm: AggregatorCredentialsForm;
  /** The unfilled form as the backend first built it; kept for parity with legacy. */
  Credentials?: AggregatorCredentialFormField[] | null;
  WealthAccountId?: number | null;
  WealthStatus?: number | null;
  Receipt?: string | null;
  HasRealtime?: boolean;
  AdvisorInstitution?: boolean;
  GUID?: string;
  OauthURL?: string | null;
  AuthToken?: string | null;
}

/**
 * URL segment per aggregator, as legacy built it (`SetupCredentials.jsx:40-51`):
 * `api/{segment}/credentials`. Yodlee is absent on purpose — it reconnects
 * through the FastLink iframe, which cannot be expressed as form fields.
 */
const GENERIC_CREDENTIALS_SEGMENTS: Record<number, string> = {
  [AggregatorId.ByAll]: 'ByAll',
  [AggregatorId.Demo]: 'demo',
};

/** Every aggregator whose connection errors the user can resolve themselves. */
const SUPPORTED_AGGREGATORS = new Set<number>([
  AggregatorId.ByAll,
  AggregatorId.Demo,
  AggregatorId.Yodlee,
]);

/** Form types the reconnect flow knows how to render or close out. */
const SUPPORTED_FORM_TYPES = new Set<AggregatorCredentialsFormType>([
  AggregatorCredentialsFormType.UpdateCredentials,
  AggregatorCredentialsFormType.MfaCredentials,
  AggregatorCredentialsFormType.AuthenticationSucceeded,
  AggregatorCredentialsFormType.NotRecoverable,
  AggregatorCredentialsFormType.CustomMessage,
]);

/** Form types that end the flow: nothing left to ask the user. */
const TERMINAL_FORM_TYPES = new Set<AggregatorCredentialsFormType>([
  AggregatorCredentialsFormType.AuthenticationSucceeded,
  AggregatorCredentialsFormType.NotRecoverable,
  AggregatorCredentialsFormType.CustomMessage,
]);

/** Terminals that mean failure — the aggregator says retrying is pointless. */
const FAILURE_TERMINAL_FORM_TYPES = new Set<AggregatorCredentialsFormType>([
  AggregatorCredentialsFormType.NotRecoverable,
  AggregatorCredentialsFormType.CustomMessage,
]);

/** Field types the user can type into, hence the ones we track state for. */
const EDITABLE_FIELD_TYPES = new Set<AggregatorCredentialsFieldType>([
  AggregatorCredentialsFieldType.Text,
  AggregatorCredentialsFieldType.Password,
  AggregatorCredentialsFieldType.RadioGroup,
  AggregatorCredentialsFieldType.Checkboxgroup,
]);

export function resolveAggregatorSegment(
  aggregatorId: number | undefined | null,
): string | null {
  if (aggregatorId == null) {
    return null;
  }
  return GENERIC_CREDENTIALS_SEGMENTS[aggregatorId] ?? null;
}

/**
 * Gates the whole connection-error feature. Aggregators outside this set have no
 * per-account reauth (Orion is advisor-group wide; WABank and Fulton are direct
 * feeds), so offering a "Fix It" button for them would be a dead end — and worse,
 * routing them into the Yodlee PUT rewrites the wealth as Yodlee.
 */
export function isSupportedAggregator(
  aggregatorId: number | undefined | null,
): boolean {
  return aggregatorId != null && SUPPORTED_AGGREGATORS.has(aggregatorId);
}

/** True when the aggregator reconnects through the declarative form, not FastLink. */
export function isGenericCredentialsAggregator(
  aggregatorId: number | undefined | null,
): boolean {
  return resolveAggregatorSegment(aggregatorId) !== null;
}

export function isSupportedFormType(
  type: AggregatorCredentialsFormType,
): boolean {
  return SUPPORTED_FORM_TYPES.has(type);
}

export function isTerminalFormType(
  type: AggregatorCredentialsFormType,
): boolean {
  return TERMINAL_FORM_TYPES.has(type);
}

export function isFailureTerminalFormType(
  type: AggregatorCredentialsFormType,
): boolean {
  return FAILURE_TERMINAL_FORM_TYPES.has(type);
}

/**
 * Non-editable fields (image challenges, links) carry a backend `Value` that must
 * round-trip untouched — for a captcha it can be the token binding the answer to
 * the image. Only editable fields get local state.
 */
export function isEditableFieldType(
  type: AggregatorCredentialsFieldType,
): boolean {
  return EDITABLE_FIELD_TYPES.has(type);
}
